import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import ProgressBar from "../components/ProgressBar";
import NBLogoWhite from "../components/NBLogoWhite";
import NavPanel from "../components/NavPanel";
import TapArea from "../components/TapArea";

import styles from "./CocoCloset.module.css";

const products = [
  { id: 1, link: "https://www.newbalance.com/pd/fresh-foam-x-ct-rally/MCHRALV1-42680.html?dwvar_MCHRALV1-42680_style=MCHRALW1utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=CTRallyMCHRALW1&ecid=so_fbig_paid_TennisMoMo_CTRallyMCHRALW1", name: "Fresh Foam X CT-Rally", desc: 'White with Navy', image: "tommy/p1.png" },
  { id: 2, link: "https://www.newbalance.com/pd/fresh-foam-x-ct-rally-v2/WCHRALV2-51724.html?dwvar_WCHRALV2-51724_style=WCHRALG2#utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=&utm_term=CTRallyv2TennisMoMo&ecid=so_fbig_paid__CTRallyv2TennisMoMo", name: "Fresh Foam X CT-Rally v2", desc: 'Sea Salt with Lime Leaf', image: "tommy/p2.png" },
  { id: 3, link: "https://www.newbalance.com/pd/new-balance-performance-polo/MT41401.html?dwvar_MT41401_style=MT41401WTutm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=PerformancePoloMT41401WT&ecid=so_fbig_paid_TennisMoMo_PerformancePoloMT41401WT#dwvar_MT41401_style=MT41401WT&pid=MT41401&quantity=1", name: "New Balance Performance Polo", desc: 'White', image: "tommy/p4.png" },
  { id: 4, link: "https://www.newbalance.com/pd/tournament-top/MT41403.html?dwvar_MT41403_style=MT41403WTutm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=TournamentTopMT41403WT&ecid=so_fbig_paid_TennisMoMo_TournamentTopMT41403WT#dwvar_MT41403_style=MT41403WT&pid=MT41403&quantity=1", name: "Tournament Top", desc: 'White', image: "tommy/p5.png" },
  { id: 5, link: "https://www.newbalance.com/pd/tournament-short/MS41401.html?dwvar_MS41401_style=MS41401WTutm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=TournamentShortMS41401WT&ecid=so_fbig_paid_TennisMoMo_TournamentShortMS41401WT#dwvar_MS41401_style=MS41401WT&pid=MS41401&quantity=1", name: "Tournament Short", desc: 'White', image: "tommy/p6.png" },
  { id: 6, link: "https://www.newbalance.com/pd/athletics-french-terry-hoodie/MT41534.html#dwvar_MT41534_style=MT41534BK&pid=MT41534&quantity=1?utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=AthleticFrenchTerryHoodieMT41534BK&ecid=so_fbig_paid_TennisMoMo_AthleticFrenchTerryHoodieMT41534BK", name: 'Athletics French Terry Hoodie', desc: 'Black', image: "tommy/p7.png" },
  { id: 7, link: "https://www.newbalance.com/pd/numeric-standard-pant-32/MP51953.html?utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=NumericStandardPant32MP51953BK&ecid=so_fbig_paid_TennisMoMo_NumericStandardPant32MP51953BK", name: 'Numeric Standard Pant 32"', desc: 'Black', image: "tommy/p8.png" }
];

const TommyCloset = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();

  // Programmatic scroll guard
  const isProgrammaticRef = useRef(false);
  const targetIndexRef = useRef(null);
  const programmaticTimerRef = useRef(null);

  const onPreviousPageClick = useCallback(() => {
    navigate("/tommy-trivia");
  }, [navigate]);

  const onNextPageClick = useCallback(() => {
    navigate("/product-poll");
  }, [navigate]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (programmaticTimerRef.current) {
        clearTimeout(programmaticTimerRef.current);
      }
    };
  }, []);

  // Initial positioning - ensure first product is centered
  useEffect(() => {
    if (carouselRef.current && itemRefs.current[0]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        itemRefs.current[0].scrollIntoView({
          behavior: "auto",
          inline: "center",
          block: "nearest"
        });
      }, 50);
    }
  }, []);

  // Scroll to the active product when activeIndex changes, only for programmatic triggers
  useEffect(() => {
    if (!isProgrammaticRef.current) return;
    if (carouselRef.current && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });
    }
  }, [activeIndex]);

  const onClickLink =  (link) => {
    umami?.track('Clicked link on Tommy Closet', { link: link });
    window.open(link)
  }

  // Detect which product is centered (only when not programmatically scrolling)
  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current) return;
      if (isProgrammaticRef.current) return; // ignore while animating to a dot-selected item
      
      const carousel = carouselRef.current;
      const carouselRect = carousel.getBoundingClientRect();
      let minDiff = Infinity;
      let newActive = 0;
      
      itemRefs.current.forEach((item, idx) => {
        if (!item) return;
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const carouselCenter = carouselRect.left + carouselRect.width / 2;
        const diff = Math.abs(itemCenter - carouselCenter);
        if (diff < minDiff) {
          minDiff = diff;
          newActive = idx;
        }
      });
      setActiveIndex(newActive);
    };
    
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", handleScroll, { passive: true });
    }
    
    return () => {
      if (carousel) {
        carousel.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);


  const onDotClick = (idx) => {
    // Engage programmatic mode to avoid feedback with scroll listener
    isProgrammaticRef.current = true;
    targetIndexRef.current = idx;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      isProgrammaticRef.current = false;
      targetIndexRef.current = null;
    }, 700);
    setActiveIndex(idx);
  };

  return (
    <div className={styles.cocoCloset}>
      <NavPanel onClick={onPreviousPageClick} />
      <NavPanel postion='right' onClick={onNextPageClick} />

      <TapArea style={styles.tapPrevious} onClick={onPreviousPageClick} />
      <TapArea style={styles.tapNext} onClick={onNextPageClick} />

      <ProgressBar current='3' />
      <NBLogoWhite />
      
      <img
        src="tommy_closet.png"
        className={styles.backgroundImg}
        alt="Tommy Closet Background"
      />
      <div className={styles.gradient}></div>

      <div className={styles.checkoutText}>Check out <br/> Tommy Paul’s Closet</div>

      {/* Carousel positioned 50px from the bottom */}
      <div className={styles.carouselWrapper}>
        <div className={styles.carouselContainer}>
          <div className={styles.carousel} ref={carouselRef}>
            {products.map((product, idx) => (
              <div
                key={product.id}

                className={
                  idx === activeIndex
                    ? `${styles.productCard} ${styles.active}`
                    : styles.productCard
                }
                ref={el => (itemRefs.current[idx] = el)}
                onClick={() => onClickLink(product.link)}
              >
                <img className={styles.productImg} src={product.image} />
                <div className={styles.productInfo}> 
                  <div className={styles.productName}>{product.name}</div>
                  <div className={styles.productDesc}>{product.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.dots}>
        {products.map((_, idx) => (
          <span
            key={idx}
            className={idx === activeIndex ? styles.dotActive : styles.dot}
            onClick={() => onDotClick(idx)}
          />
        ))}
      </div>
    </div>
  );
};

export default TommyCloset; 
